import React, { useState, SyntheticEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import { translations } from '../utils/translations';
import { FaUser, FaLock, FaEnvelope } from 'react-icons/fa';
import './Login.css'; // Import a CSS file for additional styling

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);

    // Translations hook
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const submit = async (e: SyntheticEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const { data } = await axios.post('https://api.sominnovations.xyz/users/login', {
                email,
                password
            }, { withCredentials: true });

            document.cookie = `token=${data.token}; path=/`;
            setUserName(`${data.first_name} ${data.last_name}`);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            fetchUserRole(); // Fetch user role after successful login
        } catch (error) {
            setError('Login failed: Invalid credentials');
            setLoading(false);
        }
    };

    const fetchUserRole = async () => {
        try {
            const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
            setUserRole(data.user.role); // Ensure you access the role correctly from the response
            console.log('User role:', data.user.role); // Verify role in console
            setAuthenticated(true); // Set authenticated to true after successful login
        } catch (error) {
            console.error('Failed to fetch user role:', error);
            setError('Failed to fetch user role');
        } finally {
            setLoading(false);
        }
    };

    if (authenticated) {
        return <Navigate to="/dashboard" />; // Navigate to dashboard if authenticated
    }

    return (
        <div className="auth-login-page">
            <div className="login-container">
                <div className="row rows g-0 login-form-container">
                    <div className="col-md-6 d-none d-md-block">
                        <img
                            src="/images/pos.jpg"
                            alt="Pos Logo"
                            className="login-image"
                        />
                    </div>
                    <div className="col-md-6 login-form-right">
                        <div className="text-center mb-5">
                            <h1 className="h3 mb-3 fw-bold">{t.welcome}</h1>
                            <p className="text-muted">{t.loginWelcome}</p>
                        </div>

                        <form onSubmit={submit}>
                            {error && (
                                <div className="alert alert-danger d-flex align-items-center" role="alert">
                                    <div>{error}</div>
                                </div>
                            )}

                            <div className="input-group mb-4">
                                <span className="input-group-text pe-0">
                                    <FaEnvelope className="text-muted" />
                                </span>
                                <div className="form-floating flex-grow-1">
                                    <input
                                        type="email"
                                        className="form-control border-start-0 ps-2"
                                        id="floatingInput"
                                        placeholder="name@example.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                    />
                                    <label htmlFor="floatingInput" className="ps-2">{t.email}</label>
                                </div>
                            </div>

                            <div className="input-group mb-4">
                                <span className="input-group-text pe-0">
                                    <FaLock className="text-muted" />
                                </span>
                                <div className="form-floating flex-grow-1">
                                    <input
                                        type="password"
                                        className="form-control border-start-0 ps-2"
                                        id="floatingPassword"
                                        placeholder="Password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                    />
                                    <label htmlFor="floatingPassword" className="ps-2">{t.password}</label>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="rememberMe" />
                                    <label className="form-check-label text-muted small" htmlFor="rememberMe">
                                        Remember me
                                    </label>
                                </div>
                                <Link to="/forgot" className="text-decoration-none small text-primary fw-bold">
                                    {t.forgotPassword}
                                </Link>
                            </div>

                            <button
                                className="w-100 btn btn-lg btn-primary mb-3"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Signing in...
                                    </>
                                ) : (
                                    t.login
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;

